import type { AxiosCollection, AxiosItem } from '@/types/Components/Response'
import type { FeedbackContext, FeedbackStatus, FeedbackThread } from '@/types/Models/Feedback'
import Axios from '@/services/Axios'

// Local comments stay in the checkout; hosted dev comments use the authenticated Core API.
export default class FeedbackProxy {
  // Feedback service failures must not trigger Core's global logout interceptor.
  private local = import.meta.env.DEV && import.meta.env.VITE_LOCAL_FEEDBACK === 'true'
  private endpoint = this.local ? '/__local-feedback' : '/page-feedback'
  private http = Axios.create({
    baseURL: this.local ? window.location.origin : import.meta.env.VITE_APP_API_LOCATION,
    timeout: 10000,
  })

  private submit<T>(method: 'get' | 'post' | 'patch' | 'delete', url: string, data?: object): Promise<T> {
    return this.http.request<T>({ method, url, data, headers: { Authorization: String(Axios.defaults.headers.common.Authorization || '') } })
      .then((response) => response.data)
      .catch((error) => Promise.reject(error.response?.data || { message: 'Feedback is unavailable. Your text is still here; please retry.' }))
  }

  async list(): Promise<AxiosCollection<FeedbackThread>> {
    if (this.local) return this.submit('get', this.endpoint)
    const data: FeedbackThread[] = []
    let page = 1
    let count: number
    do {
      const result = await this.submit<AxiosCollection<FeedbackThread>>('get', `${this.endpoint}?page=${page++}&limit=100`)
      count = result.count
      if (!result.data.length) break
      data.push(...result.data)
    } while (data.length < count)
    return { data: [...new Map(data.map((thread) => [thread.id, thread])).values()], count }
  }

  add(body: string, context: FeedbackContext): Promise<AxiosItem<FeedbackThread>> {
    return this.submit('post', this.endpoint, { body, context })
  }

  reply(id: string, body: string): Promise<AxiosItem<FeedbackThread>> {
    return this.submit('post', `${this.endpoint}/${id}/replies`, { body })
  }

  remove(id: string): Promise<void> {
    return this.submit('delete', `${this.endpoint}/${id}`, {})
  }

  setStatus(id: string, status: FeedbackStatus): Promise<AxiosItem<FeedbackThread>> {
    return this.submit('patch', `${this.endpoint}/${id}`, { status })
  }
}
